import { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppLayout } from "@/shared/components/AppLayout";
import { ProtectedRoute } from "@/modules/auth/components/ProtectedRoute";
import { RequirePermissionRoute } from "@/modules/auth/components/RequirePermissionRoute";
import { RoleLandingRedirect } from "@/modules/auth/components/RoleLandingRedirect";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";

const LoginPage = lazyWithRetry(() => import("@/modules/auth/pages/LoginPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("@/modules/auth/pages/ForgotPasswordPage"));
const ForgotPasswordOtpPage = lazyWithRetry(() => import("@/modules/auth/pages/ForgotPasswordOtpPage"));
const ResetPasswordPage = lazyWithRetry(() => import("@/modules/auth/pages/ResetPasswordPage"));
const DashboardPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/DashboardPage"));
const UsersListPage = lazyWithRetry(() => import("@/modules/users/pages/UsersListPage"));
const DepartmentsListPage = lazyWithRetry(() => import("@/modules/departments/pages/DepartmentsListPage"));
const ClientsListPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientsListPage"));
const ClientDetailPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientDetailPage"));
const ClientGroupDetailPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientGroupDetailPage"));
const TemplatesListPage = lazyWithRetry(() => import("@/modules/templates/pages/TemplatesListPage"));
const TemplateCreatePage = lazyWithRetry(() => import("@/modules/templates/pages/TemplateCreatePage"));
const TemplateEditorPage = lazyWithRetry(() => import("@/modules/templates/pages/TemplateEditorPage"));
const ProjectsListPage = lazyWithRetry(() => import("@/modules/projects/pages/ProjectsListPage"));
const ProjectDetailPage = lazyWithRetry(() => import("@/modules/projects/pages/ProjectDetailPage"));
const WorkUnitsListPage = lazyWithRetry(() => import("@/modules/workunits/pages/WorkUnitsListPage"));
const WorkUnitDetailPage = lazyWithRetry(() => import("@/modules/workunits/pages/WorkUnitDetailPage"));
const DocumentRegistryPage = lazyWithRetry(() => import("@/modules/documents/pages/DocumentRegistryPage"));
const ApprovalsQueuePage = lazyWithRetry(() => import("@/modules/approvals/pages/ApprovalsQueuePage"));
const DepartmentDetailPage = lazyWithRetry(() => import("@/modules/departments/pages/DepartmentDetailPage"));
const AttendancePage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/AttendancePage"));
const EmployeeAttendanceDetailPage = lazyWithRetry(
  () => import("@/modules/attendance/pages/EmployeeAttendanceDetailPage"),
);
const LocationsListPage = lazyWithRetry(() => import("@/modules/locations/pages/LocationsListPage"));
const LeavesListPage = lazyWithRetry(() => import("@/modules/leaves/pages/LeavesListPage"));
const LiveMonitorPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/LiveMonitorPage"));
const LiveMonitorUserDetailPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/LiveMonitorDetail"));
const NotificationsPage = lazyWithRetry(() => import("@/modules/notifications/pages/NotificationsPage"));
const ErrorLogPage = lazyWithRetry(() => import("@/modules/errorLog/pages/ErrorLogPage"));
const RegularisationsPage = lazyWithRetry(() => import("@/modules/regularisations/pages/RegularisationsPage"));
const MessagingConfigPage = lazyWithRetry(() => import("@/modules/messaging/pages/MessagingConfigPage"));
const ReportsPage = lazyWithRetry(() => import("@/modules/reports/pages/ReportsPage"));
const ReportViewerPage = lazyWithRetry(() => import("@/modules/reports/pages/ReportViewerPage"));
const HolidaysListPage = lazyWithRetry(() => import("@/modules/holidays/pages/HolidaysListPage"));
const DeviceChangeRequestsPage = lazyWithRetry(
  () => import("@/modules/devices/pages/DeviceChangeRequestsPage"),
);
const ClientDocsQueuePage = lazyWithRetry(() => import("@/modules/clientportal/pages/ClientDocsQueuePage"));
const ClientPortalPage = lazyWithRetry(() => import("@/modules/clientportal/pages/ClientPortalPage"));

const Loading = () => <div className="p-6 text-sm text-gray-500">Loading...</div>;

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password-otp",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ForgotPasswordOtpPage />
      </Suspense>
    ),
  },
  {
    path: "/reset-password",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    errorElement: <RouteErrorBoundary />,
    element: (
      <ProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <RoleLandingRedirect /> },
      { path: "dashboard", element: <DashboardPage /> },
      {
        path: "users",
        element: (
          <RequirePermissionRoute permission="users.read">
            <UsersListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "departments",
        element: (
          <RequirePermissionRoute permission="departments.read">
            <DepartmentsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "clients",
        element: (
          <RequirePermissionRoute permission="clients.read">
            <ClientsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "clients/:id",
        element: (
          <RequirePermissionRoute permission="clients.read">
            <ClientDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "client-groups/:id",
        element: (
          <RequirePermissionRoute permission="clientGroups.read">
            <ClientGroupDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates",
        element: (
          <RequirePermissionRoute permission="templates.read">
            <TemplatesListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates/new",
        element: (
          <RequirePermissionRoute permission="templates.create">
            <TemplateCreatePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates/:templateId",
        element: (
          <RequirePermissionRoute permission="templates.update">
            <TemplateEditorPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "projects",
        element: (
          <RequirePermissionRoute permission="projects.read">
            <ProjectsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "projects/:projectId",
        element: (
          <RequirePermissionRoute permission="projects.read">
            <ProjectDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "work-units",
        element: (
          <RequirePermissionRoute permission="workunits.read">
            <WorkUnitsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "work-units/:id",
        element: (
          <RequirePermissionRoute permission="workunits.read">
            <WorkUnitDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "documents",
        element: (
          <RequirePermissionRoute permission="documents.registry.read">
            <DocumentRegistryPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "approvals",
        element: (
          <RequirePermissionRoute
            permission={[
              "approvals.resolve",
              "workunits.request_deactivation",
              "workunits.deactivate",
            ]}
          >
            <ApprovalsQueuePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "attendance",
        element: (
          <RequirePermissionRoute
            permission={["attendance.mark", "attendance.read_self", "attendance.read_all"]}
          >
            <AttendancePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "attendance/users/:userId",
        element: (
          <RequirePermissionRoute permission="attendance.read_all">
            <EmployeeAttendanceDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "locations",
        element: (
          <RequirePermissionRoute permission="locations.read">
            <LocationsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path:"holidays",
        element:(
          <RequirePermissionRoute permission="holidays.read">
            <HolidaysListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "phone-requests",
        element: (
          <RequirePermissionRoute permission="devices.approve_change">
            <DeviceChangeRequestsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "leaves",
        element: (
          <RequirePermissionRoute permission={["leaves.read_self", "leaves.read_all"]}>
            <LeavesListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "live-monitor",
        element: (
          <RequirePermissionRoute permission="liveMonitor.view">
            <LiveMonitorPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "live-monitor/users/:userId",
        element: (
          <RequirePermissionRoute permission="liveMonitor.view">
            <LiveMonitorUserDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "notifications",
        element: (
          <RequirePermissionRoute permission="notifications.read_self">
            <NotificationsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "error-log",
        element: (
          <RequirePermissionRoute permission="errorLog.view">
            <ErrorLogPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "regularisations",
        element: <RegularisationsPage />,
      },
      {
        path: "reports",
        element: (
          <RequirePermissionRoute permission="reports.view">
            <ReportsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "reports/:reportId",
        element: (
          <RequirePermissionRoute permission="reports.view">
            <ReportViewerPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "messaging",
        element: (
          <RequirePermissionRoute permission="messaging.manage">
            <MessagingConfigPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "departments/:id",
        element: (
          <RequirePermissionRoute permission="departments.read">
            <DepartmentDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "client-docs",
        element: (
          <RequirePermissionRoute permission="clientdocs.list.global">
            <ClientDocsQueuePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "portal",
        element: (
          <RequirePermissionRoute permission="clientdocs.upload">
            <ClientPortalPage />
          </RequirePermissionRoute>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
